import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toastify from "../../../helper/toastify";
import {
  resendOtp,
  verifyOtp,
} from "../../../redux/features/auth/authSliceNew";

const LoginOtp = ({ phone }) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timer, setTimer] = useState(60);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, addressData } = useSelector((state) => state.auth);
  const [otpVerified, setOtpVerified] = useState(false);

  useEffect(() => {
    if (phone) {
      setPhoneNumber(phone);
    }
    // Reset OTP verified flag when phone changes
    setOtpVerified(false);
  }, [phone]);

  // Watch for user data changes after OTP verification
  useEffect(() => {
    if (otpVerified && user) {
      console.log("User data loaded after OTP verification:", user);
      handlePostOtpVerification();
    }
  }, [otpVerified, user]);

  // Fallback timeout in case user data doesn't load
  useEffect(() => {
    if (otpVerified) {
      const fallbackTimeout = setTimeout(() => {
        console.log("Fallback timeout triggered - checking profile with current user data");
        handlePostOtpVerification();
      }, 2000); // Reduced timeout to 2 seconds

      return () => clearTimeout(fallbackTimeout);
    }
  }, [otpVerified]);

  // Function to check if user profile is complete
  const isProfileComplete = () => {
    if (!user) {
      console.log("No user data available");
      return false;
    }
    
    const hasName = Boolean(user?.name && String(user.name).trim().length > 0);
    
    console.log("Profile check:", {
      user: user,
      hasName: hasName,
      name: user?.name,
      allUserKeys: Object.keys(user || {}),
      userType: user?.user_type,
      isNewUser: user?.is_new_user
    });
    
    // For existing users, if they have a name, they're good to go
    // Only require profile completion for truly new users without a name
    const isComplete = hasName;
    console.log("Profile is complete:", isComplete);
    return isComplete;
  };

  // Function to check if user needs profile completion
  const needsProfileCompletion = () => {
    // If no user data, they need to complete profile
    if (!user) {
      console.log("No user data - needs profile completion");
      return true;
    }
    
    // Check if profile is incomplete
    const needsCompletion = !isProfileComplete();
    console.log("Needs profile completion:", needsCompletion);
    return needsCompletion;
  };

  // Handle post OTP verification logic
  const handlePostOtpVerification = () => {
    console.log("Handling post OTP verification...");
    console.log("Current user data:", user);
    
    if (needsProfileCompletion()) {
      console.log("User needs profile completion - showing modal");
      // User needs profile completion (new user or existing user with incomplete profile)
      localStorage.setItem("just_logged_in", "1");
      // Show profile modal immediately, don't navigate yet
      const profileModalEl = document.getElementById("firsttime-profile-modal");
      if (profileModalEl && window.bootstrap) {
        const profileModalInstance = window.bootstrap.Modal.getInstance(profileModalEl) || new window.bootstrap.Modal(profileModalEl);
        profileModalInstance.show();
      }
    } else {
      console.log("User has complete profile - navigating to home page");
      // User has complete profile - go directly to home page
      localStorage.removeItem("just_logged_in");
      // Reload the page to ensure fresh state
      window.location.href = "/";
    }
  };

  // Handle Otp verification
  const handleOtpVerification = async (e) => {
    e.preventDefault();
    if (!otp) {
      toastify.error("Please enter OTP");
      return;
    }
    setLoading(true);
    try {
      const response = await dispatch(
        verifyOtp({ mobile: phoneNumber, otp: otp })
      );
      if (response.success) {
        const modalElement = document.getElementById("otp-modal");
        if (modalElement) {
          const modalInstance =
            window.bootstrap.Modal.getInstance(modalElement) || new window.bootstrap.Modal(modalElement);
          modalInstance.hide();
        }
        
        // Set flag to trigger useEffect that watches for user data
        setOtpVerified(true);
        console.log("OTP verification successful, waiting for user data to load...");
      }
    } catch (error) {
      console.log("error");
    } finally {
      setLoading(false);
    }
  };

  // Handle Resend otp
  const handleResendOtp = async () => {
    const response = await dispatch(resendOtp({ mobile: phoneNumber }));
    if (response.success) {
      setTimer(60);
    }
  };

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    const loginModalEl = document.getElementById("otp-modal");
    const handleModalHidden = () => {
      setOtp("");
      // Avoid full reload to prevent losing modal state
    };
    if (loginModalEl) {
      loginModalEl.addEventListener("hidden.bs.modal", handleModalHidden);
    }
    return () => {
      if (loginModalEl) {
        loginModalEl.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
    };
  }, []);

  return (
    <>
      <div
        className="modal fade login-modal"
        id="otp-modal"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content simple-otp-modal">
            <div className="modal-header simple-otp-header">
              <div className="simple-otp-title">
                <h3 className="modal-title" id="exampleModalLabel">
                  Verify Your Number
                </h3>
                <p className="simple-otp-subtitle">
                  We've sent a verification code to your mobile
                </p>
              </div>
            </div>
            <div className="modal-body simple-otp-body">
              <form onSubmit={handleOtpVerification} className="simple-otp-form">
                <div className="simple-otp-illustration">
                  <div className="simple-otp-phone">
                    <i className="fas fa-mobile-alt"></i>
                  </div>
                </div>
                
                <div className="simple-otp-input-section">
                  <div className="simple-otp-phone-display">
                    <span>+91 {phoneNumber}</span>
                  </div>
                  
                  <div className="simple-otp-input-group">
                    <input
                      type="text"
                      className="form-control simple-otp-input"
                      placeholder="Enter 4-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      maxLength="4"
                    />
                  </div>
                  
                  <div className="simple-otp-button-group">
                    <button
                      className="thm-btn fill-btn simple-otp-verify-btn"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="simple-otp-spinner">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span>Verifying...</span>
                        </span>
                      ) : (
                        <span>Verify OTP</span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="simple-otp-footer">
                  <div className="simple-otp-resend-section">
                    <p className="simple-otp-resend-text">
                      Didn't receive the code?{" "}
                      <button 
                        type="button"
                        className="simple-otp-resend-btn"
                        onClick={handleResendOtp} 
                        disabled={timer > 0}
                      >
                        {timer > 0 ? `Resend in ${timer}s` : "Resend OTP"}
                      </button>
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginOtp;